import React from 'react';
import { faFilePdf, faMemoCircleCheck, faUserClock, faXmark } from '@fortawesome/pro-light-svg-icons';
import { AttendanceSheetStateDto } from '../../../../../api-service/clients';
import { FloatingActionButtonBar } from '../../../../../components/area-layout/floating-action-button-bar';
import { FloatingActionButton } from '../../../../../components/area-layout/floating-action-button';
import { FabDivider } from '../../../../../components/area-layout/fab-divider';

interface IAttendanceProtocolFabProps {
  attendanceSheetState?: AttendanceSheetStateDto;
  allChecksComplete: boolean;
  hasAttendanceChecks: boolean;
  isLocked: boolean;
  canEditProtocol: boolean;
  requiresApproval: boolean;
  lessonHasBegun: boolean;
  allowApprovalBeforeLessonStart: boolean;
  hasRestrictedCitizens: boolean;
  isGeneratingPdf: boolean;
  onRegisterNoShows: () => void;
  onSubmit: () => void;
  onApprove: () => void;
  onCreatePdf: () => void;
}

export function AttendanceProtocolFab(props: IAttendanceProtocolFabProps) {
  const {
    attendanceSheetState,
    allChecksComplete,
    hasAttendanceChecks,
    isLocked,
    canEditProtocol,
    requiresApproval,
    lessonHasBegun,
    allowApprovalBeforeLessonStart,
    hasRestrictedCitizens,
    isGeneratingPdf,
    onRegisterNoShows,
    onSubmit,
    onApprove,
    onCreatePdf,
  } = props;
  const isSubmitted = attendanceSheetState === AttendanceSheetStateDto.Submitted;
  const isReadyForApproval = attendanceSheetState === AttendanceSheetStateDto.ReadyForApproval;
  const awaitsReadyForApproval = requiresApproval && !isReadyForApproval && !isSubmitted;
  const lessonStartBlocksApproval = !lessonHasBegun && !allowApprovalBeforeLessonStart;
  const approveDisabled = isSubmitted || hasRestrictedCitizens || lessonStartBlocksApproval || !allChecksComplete;

  function getApproveDisabledTooltip() {
    if (isSubmitted) {
      return 'Protokollen er allerede godkendt';
    }
    if (hasRestrictedCitizens) {
      return awaitsReadyForApproval
        ? 'Protokollen kan ikke sendes til godkendelse, da den indeholder borgere, du ikke har adgang til'
        : 'Protokollen kan ikke godkendes, da den indeholder borgere, du ikke har adgang til';
    }
    if (lessonStartBlocksApproval) {
      return awaitsReadyForApproval ? 'Protokollen kan først sendes til godkendelse, når lektionen er i gang' : 'Protokollen kan først godkendes, når lektionen er i gang';
    }
    return awaitsReadyForApproval ? 'Alle borgere skal have en fremmødeværdi, før listen kan sendes til godkendelse' : 'Alle borgere skal have en fremmødeværdi før godkendelse';
  }

  function handleApprove() {
    if (isReadyForApproval) {
      onApprove();
    } else {
      onSubmit();
    }
  }

  return (
    <FloatingActionButtonBar>
      {canEditProtocol && hasAttendanceChecks && (
        <FloatingActionButton
          buttonText={
            <span>
              <span>Tjek for </span>
              <br />
              udeblivelse
            </span>
          }
          ariaLabel="Tjek for udeblivelse"
          icon={faUserClock}
          onClick={onRegisterNoShows}
          disabled={isLocked}
          disabledTooltipText="Protokollen er godkendt"
        />
      )}
      {canEditProtocol && (
        <>
          <FloatingActionButton
            buttonText={
              awaitsReadyForApproval ? (
                <span>
                  <span>Klar til </span>
                  <br />
                  godkendelse
                </span>
              ) : (
                <span>
                  <span>Godkend </span>
                  <br />
                  fremmøde
                </span>
              )
            }
            ariaLabel={awaitsReadyForApproval ? 'Klar til godkendelse' : 'Godkend fremmøde'}
            icon={faMemoCircleCheck}
            onClick={handleApprove}
            disabled={approveDisabled}
            disabledTooltipText={getApproveDisabledTooltip()}
          />
          <FabDivider />
          <FloatingActionButton buttonText="Dan PDF" ariaLabel="Dan PDF" icon={faFilePdf} onClick={onCreatePdf} loading={isGeneratingPdf} inverted />
        </>
      )}
      <FloatingActionButton buttonText="Luk" icon={faXmark} onClick={() => window.close()} inverted />
    </FloatingActionButtonBar>
  );
}
